import { IDropdownOption } from '@fluentui/react';
import {
  Button,
  buttonStylesPrimary,
  debounce,
  Dropdown,
  getDescriptionForItemCount,
  IconButton,
  iconButtonStylesToggle,
  ISearchProps,
  PageHeader,
  pageHeaderStylesSecondary,
  Search,
  useClassNames,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { ProjectList } from '../../../components/ProjectList/ProjectList';
import { ProjectPanel } from '../../../components/ProjectPanel/ProjectPanel';
import {
  ActivePermission,
  Project,
  ProjectSearchRequestProjectSortField,
  SortDirection,
} from '../../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { ContentsTitle, SortInfo } from '../../../featurestore/models';
import { featurestorePageClassName } from '../../../featurestore/styles';
import { RoutePaths } from '../../../pages/Routes';
import { useSearchParams } from '../../../utils/hooks';
import { IProjectsClassNames, IProjectsStyles, projectsStylesDefault } from './Projects.styles';

const sortOptions: IDropdownOption[] = [
  { key: ProjectSearchRequestProjectSortField.PROJECT_SORT_FIELD_NAME, text: 'Name' },
  { key: ProjectSearchRequestProjectSortField.PROJECT_SORT_FIELD_DESCRIPTION, text: 'Description' },
  { key: ProjectSearchRequestProjectSortField.PROJECT_SORT_FIELD_ACCESS_MODIFIER, text: 'Access Modifier' },
  { key: ProjectSearchRequestProjectSortField.PROJECT_SORT_FIELD_AUTHOR, text: 'Author' },
  { key: ProjectSearchRequestProjectSortField.PROJECT_SORT_FIELD_LAST_UPDATE, text: 'Last Updated' },
];

const sortDirectionOptions: IDropdownOption[] = [
  { key: SortDirection.SORT_ASC, text: 'Asc' },
  { key: SortDirection.SORT_DESC, text: 'Desc' },
];

const defaultSortInfo = {
  field: '',
  direction: SortDirection.SORT_ASC,
};

interface IProjectsProps {
  ownedOnly?: boolean;
}

export const Projects = ({ ownedOnly }: IProjectsProps) => {
  const history = useHistory();
  const [params] = useSearchParams();
  const [searchText, setSearchText] = useState(params.get('name') || '');
  const [sortInfo, setSortInfo] = useState<SortInfo>({ ...defaultSortInfo });
  const [isCardView, setIsCardView] = useState(true);
  const [description, setDescription] = useState('');
  const [showProjectPanel, setShowProjectPanel] = useState(false);
  const [project, setProject] = useState<Project | undefined>(undefined);

  const changeSearchText = useCallback((text: string) => {
    setSearchText(text);
  }, []);
  const onSearchTextChange = useMemo(() => debounce(300, changeSearchText), [changeSearchText]);
  // TODO: this may cause the render ProjectList twice since the API call update Projects.tsx and ProjectList.tsx together.
  const onUpdateData = useCallback((count: number, pageSize: number, pageToken: string, name: ContentsTitle) => {
    setDescription(getDescriptionForItemCount(count, pageSize, pageToken, name));
  }, []);
  const searchProps: ISearchProps = {
    placeholder: 'Search by name',
    defaultSearchText: params.get('name') || '',
    onSearchTextChange,
  };
  const classNames = useClassNames<IProjectsStyles, IProjectsClassNames>(
    'Projects',
    projectsStylesDefault,
    undefined,
    featurestorePageClassName
  );
  useEffect(() => {
    return () => {
      setSearchText('');
    };
  }, [params]);

  return (
    <div className={classNames.root} data-test="projects">
      <PageHeader
        styles={pageHeaderStylesSecondary}
        pageTitle={ownedOnly ? ContentsTitle.myProjects : ContentsTitle.allProjects}
        description={description}
        actions={
          <Button
            styles={buttonStylesPrimary}
            text="Create Project"
            onClick={() => {
              setProject(undefined);
              setShowProjectPanel(true);
            }}
          />
        }
        secondaryContents={<Search {...searchProps} style={{ width: 641 }} />}
        secondaryActions={
          <div style={{ display: 'flex', gap: '8px' }}>
            {isCardView && (
              <>
                <Dropdown
                  data-test="dropdown-sort"
                  styles={{ root: { width: '150px' } }}
                  placeholder="Sort By"
                  options={sortOptions}
                  selectedKey={sortInfo?.field}
                  onChange={(_e, option) => {
                    if (option) {
                      setSortInfo({ ...sortInfo, field: option.key as ProjectSearchRequestProjectSortField });
                    }
                  }}
                />
                <Dropdown
                  data-test="dropdown-sort-direction"
                  placeholder="Sort Direction"
                  options={sortDirectionOptions}
                  selectedKey={sortInfo?.direction}
                  onChange={(_e, option) => {
                    if (option) {
                      setSortInfo({ ...sortInfo, direction: option.key as SortDirection });
                    }
                  }}
                />
              </>
            )}
            <div style={{ paddingTop: '6px' }}>
              <IconButton
                style={{ marginRight: 8 }}
                styles={iconButtonStylesToggle}
                iconName="ExpandMenu"
                checked={isCardView}
                onClick={() => {
                  setSortInfo({ ...defaultSortInfo });
                  setIsCardView(true);
                }}
              />
              <IconButton
                styles={iconButtonStylesToggle}
                iconName="CollapseMenu"
                checked={!isCardView}
                onClick={() => {
                  setSortInfo({ ...defaultSortInfo });
                  setIsCardView(false);
                }}
              />
            </div>
          </div>
        }
      />
      <ProjectList
        requiredPermission={ownedOnly ? ActivePermission.ACTIVE_PERMISSION_OWNER : undefined}
        isCardView={isCardView}
        searchText={searchText}
        id={params.get('id') || ''}
        sortInfo={sortInfo}
        onUpdateData={onUpdateData}
      />
      <ProjectPanel
        show={showProjectPanel}
        onDismiss={(project) => {
          setShowProjectPanel(false);
          if (project) {
            history.push(`${RoutePaths.FEATURESTORE_PROJECT}/${project.id}`);
          }
        }}
        data={project}
      />
    </div>
  );
};
